import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';

interface Group {
  id: string;
  name: string;
  description: string;
  groupType: string;
  isPrivate: boolean;
  isActive: boolean;
  memberCount: number;
  maxMembers?: number;
  color?: string;
  icon?: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType?: string;
  isAttorney: boolean;
  isActive: boolean;
  roles: string[];
}

interface GroupMember {
  id: string;
  groupId: string;
  userId: string;
  groupRole: string;
  isActive: boolean;
  joinedAt: string;
  user: User;
}

interface GroupMembersModalProps {
  group: Group;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
  group,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  // Fetch group members
  const { data: groupMembers, isLoading: membersLoading, error: membersError } = useQuery({
    queryKey: ['group-members', group.id],
    queryFn: async () => {
      const response = await apiClient.get(`/admin/groups/${group.id}/members`);
      return response.data.members;
    },
    enabled: isOpen,
    refetchOnMount: 'always',  // ← Force refetch when modal opens
    staleTime: 0,              // ← Consider data immediately stale
    gcTime: 0,                 // ← Don't cache data when modal closes
    retry: (failureCount, error: any) => {
      // If group not found, clear all caches and don't retry
      if (error?.response?.status === 404 || error?.message?.includes('Group not found')) {
        console.warn(`Group ${group.id} not found, clearing caches`);
        queryClient.clear(); // Clear all caches
        return false;
      }
      return failureCount < 3;
    }
  });

  // Fetch all available users
  const { data: allUsersData, isLoading: usersLoading } = useQuery({
    queryKey: ['all-users-for-group'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/users?limit=100&isActive=true');
      return response.data;
    },
    enabled: isOpen
  });

  // Add user to group mutation
  const addMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/admin/groups/${group.id}/members`, {
        userId: userId,
        groupRole: 'member',
        receiveGroupNotifications: true
      });
      return userId; // Return userId for cache invalidation
    },
    onSuccess: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['group-members', group.id] });
      queryClient.invalidateQueries({ queryKey: ['user-groups', userId] }); // ← Cross-invalidate User Management tool cache
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['all-users-for-group'] });
    }
  });

  // Remove user from group mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/admin/groups/${group.id}/members/${userId}`);
      return userId; // Return userId for cache invalidation
    },
    onSuccess: (userId) => {
      queryClient.invalidateQueries({ queryKey: ['group-members', group.id] });
      queryClient.invalidateQueries({ queryKey: ['user-groups', userId] }); // ← Cross-invalidate User Management tool cache
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['all-users-for-group'] });
    }
  });

  const handleAddMember = (userId: string) => {
    addMemberMutation.mutate(userId);
  };

  const handleRemoveMember = (userId: string, userName: string) => {
    if (window.confirm(`Are you sure you want to remove ${userName} from this group? This will also remove them from the corresponding Keycloak group.`)) {
      removeMemberMutation.mutate(userId);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleColors: { [key: string]: string } = {
      'admin': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'manager': 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
      'member': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    
    return (
      <Badge variant="outline" className={roleColors[role] || roleColors['member']}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const getUserTypeBadge = (userType?: string) => {
    if (!userType) {
      return (
        <Badge variant="secondary">
          Admin
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        {userType.charAt(0).toUpperCase() + userType.slice(1)}
      </Badge>
    );
  };

  const getUserRoleBadges = (roles?: string[]) => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return null;
    }
    return (
      <div className="flex gap-1">
        {roles.map((role, index) => (
          <Badge key={`${role}-${index}`} variant="outline" className="text-xs">
            {role}
          </Badge>
        ))}
      </div>
    );
  };

  const renderUserInfo = (user: User | null | undefined, prefix = '') => {
    if (!user) {
      return `${prefix}Unknown User`;
    }
    const firstName = user.firstName || 'Unknown';
    const lastName = user.lastName || 'User';
    return `${prefix}${firstName} ${lastName}`;
  };

  // Handle phantom groups that don't exist in database
  if (membersError && (membersError as any)?.response?.status === 404) {
    console.warn(`Group ${group.id} (${group.name}) not found in database, clearing all caches`);
    queryClient.clear(); // Nuclear cache clear
    onClose(); // Close modal
    return null;
  }

  if (!isOpen) return null;

  const memberUserIds = groupMembers?.map((member: GroupMember) => member.userId) || [];
  const availableUsers = allUsersData?.users?.filter((user: User) => 
    !memberUserIds.includes(user.id) &&
    user.isActive &&
    (!searchTerm || 
      (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  ) || [];

  const filteredMembers = groupMembers?.filter((member: GroupMember) =>
    !searchTerm || 
    (member.user && (
      (member.user.firstName && member.user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.user.lastName && member.user.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.user.email && member.user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    ))
  ) || [];

  const isAtCapacity = group.maxMembers && groupMembers?.length >= group.maxMembers;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              {group.icon && <span className="mr-2 text-lg">{group.icon}</span>}
              Manage Members for {group.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Add or remove users from this group. Changes will sync with Keycloak automatically.
              {group.maxMembers && (
                <span className="ml-2 font-medium">
                  ({filteredMembers.length} / {group.maxMembers} members)
                </span>
              )}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Members */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <UsersIcon className="h-5 w-5 mr-2" />
              Current Members ({filteredMembers.length})
            </h3>
            
            {membersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No members match your search' : 'No members in this group'}
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredMembers.map((member: GroupMember) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    <div className="flex items-center flex-1">
                      <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {renderUserInfo(member.user, '')}
                          {member.user?.isAttorney && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Attorney
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {member.user?.email || 'No email available'}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {getRoleBadge(member.groupRole)}
                          {member.user && getUserTypeBadge(member.user.userType)}
                          <span className="text-xs text-gray-400">
                            Joined {new Date(member.joinedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(member.userId, renderUserInfo(member.user, ''))}
                      disabled={removeMemberMutation.isPending}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Users */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              Available Users ({availableUsers.length})
              {isAtCapacity && (
                <Badge variant="destructive" className="ml-2">
                  At Capacity
                </Badge>
              )}
            </h3>
            
            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No available users match your search' : 
                 isAtCapacity ? 'Group is at maximum capacity' : 'All users are already members'}
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableUsers.map((user: User) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    <div className="flex items-center flex-1">
                      <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {renderUserInfo(user, '')}
                          {user.isAttorney && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Attorney
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {getUserTypeBadge(user.userType)}
                          {getUserRoleBadges(user.roles)}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddMember(user.id)}
                      disabled={addMemberMutation.isPending || !!isAtCapacity}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            onClick={() => {
              onSuccess();
              onClose();
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}; 